import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { ProductTileContainer } from "@faire/web-api/indigofair/data/ProductTileContainer";
import React from "react";

import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { PAGE_TYPE } from "@faire/retailer/consts/PAGE_TYPE";
import { ProductTileClickImpressionProvider } from "@faire/retailer/hooks/ProductTileImpression/ProductTileClickImpressionProvider";
import { ProductTileViewImpressionProvider } from "@faire/retailer/hooks/ProductTileImpression/ProductTileViewImpressionProvider";
import { Category } from "@faire/retailer/models/Category/Category";
import { CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME } from "@faire/retailer/services/consts";
import { useCategoryPageFilterKeys } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageFilterKeys";
import { useCategoryPageProductFirstSearchResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useCategoryPageProductFirstSearchResults";
import { MarketplaceProductList } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/ProductFirst/__internal__/MarketplaceProductList/MarketplaceProductList";
import { useShouldShowVerticalImages } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/ProductFirst/__internal__/useShouldShowVerticalImages";
import { useOneIndexedPageNumber } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useOneIndexedPageNumber";

export const ProductFirstResults = ({
  categoryPageLoadTimingRef,
  requestId,
  headerText,
  categoryModel,
  isLoading,
  isScrollingToTop,
  onFirstBatchVisible,
  shouldRenderExtraTileOnXLScreen,
}: {
  categoryPageLoadTimingRef: React.Ref<HTMLDivElement> | undefined;
  requestId?: string;
  headerText?: string;
  categoryModel?: Category;
  isLoading: boolean;
  isScrollingToTop?: boolean;
  onFirstBatchVisible?: () => void;
  shouldRenderExtraTileOnXLScreen?: boolean;
}) => {
  const pageNumberStartingWithOne = useOneIndexedPageNumber();
  const productFirstSearchResults = useCategoryPageProductFirstSearchResults();
  const showVerticalImagery = useShouldShowVerticalImages();
  const filterKeys = useCategoryPageFilterKeys();

  return (
    <div style={{ position: "relative" }} ref={categoryPageLoadTimingRef}>
      <ProductTileViewImpressionProvider
        context={`${ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID}|${
          categoryModel?.mostSpecificPathIdentifier ?? ""
        }`}
        requestId={requestId}
      >
        <TrackedLinkContext.Provider
          value={{
            type: IClickInfo.Type.PRODUCT,
            requestId: requestId,
            containerName: `${CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME}_old`,
          }}
        >
          <ProductTileClickImpressionProvider
            context={`${ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID}|${
              categoryModel?.mostSpecificPathIdentifier ?? ""
            }`}
            productTileContainer={
              ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID
            }
            requestId={requestId}
            containerName={CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME}
            isNewProductsCategory={categoryModel?.isNewProducts}
          >
            <MarketplaceProductList
              title={headerText}
              pageType={PAGE_TYPE.CATEGORY}
              isLoading={isLoading}
              category={categoryModel}
              filterKeys={filterKeys}
              items={isLoading ? [] : productFirstSearchResults}
              pageNumber={pageNumberStartingWithOne}
              showVerticalImagery={showVerticalImagery}
              requestId={requestId}
              recordAdImpression
              isScrollingToTop={isScrollingToTop}
              onFirstBatchVisible={onFirstBatchVisible}
              itemsPerRow={
                shouldRenderExtraTileOnXLScreen
                  ? { mobileAndAbove: 2, tabletAndAbove: 4, xLargeAndAbove: 5 }
                  : undefined
              }
            />
          </ProductTileClickImpressionProvider>
        </TrackedLinkContext.Provider>
      </ProductTileViewImpressionProvider>
    </div>
  );
};
